import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { g, pu, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { usePlan } from "@/context/PlanContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SetupWizard } from "@/pages/Setup";
import { Save, Lock, Wand2, ChefHat } from "lucide-react";

export default function Funzioni() {
  const { refresh, departments: activeDepts } = useApp();
  const { askUpgrade, handleError } = usePlan();
  const [data, setData] = useState(null);
  const [on, setOn] = useState([]);
  const [depts, setDepts] = useState([]);
  const [wizard, setWizard] = useState(false);

  const load = useCallback(async () => {
    const d = await g("/profile");
    setData(d);
    setOn(d.modules.filter((m) => m.active).map((m) => m.id));
    setDepts(d.departments);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  if (!data) return <p className="text-sm text-slate-500">Caricamento…</p>;

  const toggle = (m) => {
    if (m.locked) return askUpgrade(m.feature, `${m.label} richiede un piano superiore`, "pro");
    if (m.core) return toast.info(`${m.label} è una funzione base e resta sempre attiva`);
    setOn((s) => (s.includes(m.id) ? s.filter((x) => x !== m.id) : [...s, m.id]));
  };

  const save = async () => {
    try {
      await pu("/profile/modules", { modules: on, departments: depts });
      await refresh();
      await load();
      toast.success("Funzioni aggiornate");
    } catch (e) {
      if (!handleError(e)) toast.error(errMsg(e));
    }
  };

  const prof = data.profile;

  return (
    <div className="fade-up max-w-3xl space-y-5" data-testid="funzioni-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">Configurazione</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">Funzioni attive</h1>
        <p className="mt-1 text-sm text-slate-500">Accendi o spegni i moduli: quelli spenti scompaiono dal menu, ma non perdi nulla.</p>
      </div>

      <Card className="flex flex-wrap items-center justify-between gap-3 p-5" data-testid="profile-card">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Profilo attività</p>
          <p className="mt-1 text-sm font-semibold" data-testid="profile-activities">
            {prof?.activity_labels?.join(" · ") || "Profilo non ancora impostato"}
          </p>
          {prof && (
            <p className="text-xs text-slate-500">
              {prof.services?.join(", ")} · squadra {prof.team_size} · piano consigliato {prof.suggested_plan_name}
            </p>
          )}
        </div>
        <Button variant="outline" onClick={() => setWizard(true)} data-testid="redo-quiz-btn">
          <Wand2 className="mr-1.5 h-4 w-4" /> Rifai il test attività
        </Button>
      </Card>

      <Card className="p-5" data-testid="departments-card">
        <Label className="flex items-center gap-2 text-sm font-semibold">
          <ChefHat className="h-4 w-4" /> Reparti che ricevono le comande
        </Label>
        <div className="mt-3 flex flex-wrap gap-2">
          {data.all_departments.map((d) => (
            <button
              key={d}
              data-testid={`toggle-dept-${d}`}
              onClick={() => setDepts((s) => (s.includes(d) ? s.filter((x) => x !== d) : [...s, d]))}
              className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                depts.includes(d) ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600"
              }`}
            >
              {d}
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs text-slate-500">Attivi ora: {activeDepts.join(", ")}</p>
      </Card>

      <div className="space-y-2">
        {data.modules.map((m) => (
          <Card key={m.id} className="flex items-center justify-between gap-3 p-4" data-testid={`module-${m.id}`}>
            <div className="min-w-0">
              <p className="flex items-center gap-2 text-sm font-semibold">
                {m.label}
                {m.core && <span className="rounded bg-slate-100 px-1.5 text-[9px] font-bold uppercase text-slate-500">base</span>}
                {m.locked && (
                  <span className="flex items-center gap-1 rounded bg-amber-400/90 px-1.5 text-[9px] font-bold uppercase text-slate-900">
                    <Lock className="h-2.5 w-2.5" /> Pro
                  </span>
                )}
              </p>
              <p className="text-xs text-slate-500">{m.desc}</p>
            </div>
            <Switch
              checked={on.includes(m.id) && !m.locked}
              onCheckedChange={() => toggle(m)}
              data-testid={`module-switch-${m.id}`}
            />
          </Card>
        ))}
      </div>

      <Button className="h-12 w-full" onClick={save} data-testid="save-modules-btn">
        <Save className="mr-1.5 h-4 w-4" /> Salva funzioni attive
      </Button>

      <Dialog open={wizard} onOpenChange={setWizard}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto p-0 [&>button]:hidden" data-testid="wizard-dialog">
          <DialogTitle className="sr-only">Rifai il test attività</DialogTitle>
          <DialogDescription className="sr-only">Aggiorna il profilo del locale e le funzioni attive</DialogDescription>
          <SetupWizard
            onClose={() => setWizard(false)}
            onDone={async () => {
              setWizard(false);
              await refresh();
              await load();
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
